import type { ComponentType, SVGProps } from "react"
import {
	IconBanOutline18,
	IconCircleCheckOutline18,
	IconCircleMinusOutline18,
} from "nucleo-ui-outline-18"

type StatusIcon = ComponentType<SVGProps<SVGSVGElement>>

export type ContactEmailStatus = "blocked" | "subscribed" | "unsubscribed"

export const contactEmailStatus = (contact: {
	isEmailSuppressed: boolean
	isSubscribed: boolean
}): ContactEmailStatus => {
	if (contact.isEmailSuppressed) return "blocked"
	return contact.isSubscribed ? "subscribed" : "unsubscribed"
}

export const EMAIL_STATUS_LABEL: Record<ContactEmailStatus, string> = {
	blocked: "Email blocked",
	subscribed: "Subscribed",
	unsubscribed: "Unsubscribed",
}

export const EMAIL_STATUS_ICON: Record<ContactEmailStatus, StatusIcon> = {
	blocked: IconBanOutline18,
	subscribed: IconCircleCheckOutline18,
	unsubscribed: IconCircleMinusOutline18,
}

export const EMAIL_STATUS_ICON_CLASS: Record<ContactEmailStatus, string> = {
	blocked: "text-red-500",
	subscribed: "text-green-600 dark:text-green-500",
	unsubscribed: "text-tertiary",
}

export const EMAIL_STATUS_TOOLTIP: Record<ContactEmailStatus, string> = {
	blocked:
		"This address bounced or was marked as spam, so we stopped sending to it",
	subscribed: "Subscribed to emails",
	unsubscribed: "Unsubscribed from emails",
}

export const companyLabel = (company?: {
	name?: string | null
	domains?: readonly string[] | null
}) => company?.name || company?.domains?.[0] || ""

export const buildCompanyLabelMap = (
	companies: readonly {
		id: string
		name?: string | null
		domains?: readonly string[] | null
	}[],
) => {
	const map: Record<string, string> = {}
	for (const company of companies) {
		const label = companyLabel(company)
		if (label) map[company.id] = label
	}
	return map
}

export const formatThreadCount = (count: number | undefined) =>
	count && count > 0 ? String(count) : "0"

export interface PeopleCompany {
	id: string
	label: string
	logoUrl: string | null
}

export const buildCompanyIndex = (
	companies: readonly {
		id: string
		name?: string | null
		domains?: readonly string[] | null
		logoUrl?: string | null
	}[],
): Record<string, PeopleCompany> => {
	const index: Record<string, PeopleCompany> = {}
	for (const company of companies) {
		const label = companyLabel(company)
		if (!label) continue
		index[company.id] = {
			id: company.id,
			label,
			logoUrl: company.logoUrl ?? null,
		}
	}
	return index
}

export const groupLinksByContact = (
	links: readonly { contactId: string; companyId: string }[],
): Record<string, string[]> => {
	const grouped: Record<string, string[]> = {}
	for (const link of links) {
		const ids = (grouped[link.contactId] ??= [])
		if (!ids.includes(link.companyId)) ids.push(link.companyId)
	}
	return grouped
}

/** Primary first so the row label always names the company every other surface shows. */
export const resolveContactCompanies = ({
	primaryCompanyId,
	linkedCompanyIds,
	companyIndex,
}: {
	primaryCompanyId?: string | null
	linkedCompanyIds?: readonly string[]
	companyIndex: Record<string, PeopleCompany>
}): PeopleCompany[] => {
	const ordered: PeopleCompany[] = []
	const seen = new Set<string>()
	const push = (id: string | null | undefined) => {
		if (!id || seen.has(id)) return
		const company = companyIndex[id]
		if (!company) return
		seen.add(id)
		ordered.push(company)
	}
	push(primaryCompanyId)
	for (const id of linkedCompanyIds ?? []) push(id)
	return ordered
}

interface RowCompany {
	id: string
	name?: string | null
	domains?: readonly string[] | null
	logoUrl?: string | null
}

/** A row's companies from the relations its page query loads, primary first. */
export const rowCompanies = (contact: {
	companyId?: string | null
	company?: RowCompany | null
	companyLinks?: readonly { companyId: string; company?: RowCompany | null }[]
}): PeopleCompany[] => {
	const links = contact.companyLinks ?? []
	const known = [contact.company, ...links.map((link) => link.company)].filter(
		(company): company is RowCompany => !!company,
	)
	return resolveContactCompanies({
		primaryCompanyId: contact.companyId,
		linkedCompanyIds: links.map((link) => link.companyId),
		companyIndex: buildCompanyIndex(known),
	})
}
